import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MdHome, MdConstruction } from 'react-icons/md';
import PublicHeader from '../components/layout/PublicHeader';
import { FREE_TOOLS } from '../data/freeTools';

const NotFoundPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>Page Not Found | Focuzon</title>
        <meta name="description" content="The page you're looking for doesn't exist. Explore Focuzon's free tools, blog, and focus sessions." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="min-h-screen bg-[#FFF8F0]">
        <PublicHeader />
        <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-orange-100 text-orange-600 mb-6">
              <MdConstruction className="w-10 h-10" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Page not found
            </h1>
            <p className="text-lg text-gray-600 max-w-xl mx-auto">
              The page you're looking for doesn't exist or has been moved. Here are some helpful links to get you back on track.
            </p>
          </div>

          {/* CTA - Primary */}
          <section className="mb-12 p-8 bg-white rounded-2xl border border-orange-100 shadow-sm text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Ready to focus?</h2>
            <p className="text-gray-600 mb-6">
              Start a structured focus session with Focuzon. Pomodoro timers, deep work, and task management.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="inline-flex items-center gap-2 px-8 py-4 bg-orange-600 text-white font-semibold rounded-xl hover:bg-orange-700 transition-colors shadow-lg shadow-orange-200"
            >
              Start Focus Session
            </button>
          </section>

          {/* Links to Tools */}
          <section className="mb-12">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Free Tools</h2>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {FREE_TOOLS.filter((t) => !t.requiresLogin).map((tool) => {
                const Icon = tool.icon;
                return (
                  <li key={tool.id}>
                    <Link
                      to={tool.path}
                      className="flex items-center gap-3 p-4 bg-white rounded-xl border border-orange-100 hover:border-orange-200 hover:shadow-md transition-all"
                    >
                      <div className="p-2 rounded-lg bg-orange-50">
                        <Icon className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <span className="font-medium text-gray-900">{tool.name}</span>
                        <p className="text-sm text-gray-500">{tool.description}</p>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
            <Link
              to="/tools"
              className="inline-block mt-4 text-orange-600 font-medium hover:text-orange-700"
            >
              View all tools →
            </Link>
          </section>

          {/* Links to Blog */}
          <section className="mb-12">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Blog</h2>
            <p className="text-gray-600 mb-4">
              Productivity tips, deep work strategies, and focus insights from the Focuzon team.
            </p>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 px-6 py-3 bg-orange-50 text-orange-700 font-medium rounded-xl hover:bg-orange-100 transition-colors"
            >
              Explore the Blog
            </Link>
          </section>

          {/* Back to Home */}
          <div className="text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-gray-600 hover:text-orange-600 font-medium transition-colors"
            >
              <MdHome className="w-5 h-5" />
              Back to Home
            </Link>
          </div>
        </main>
      </div>
    </>
  );
};

export default NotFoundPage;
